import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import { useAuth } from "../../../context/AuthContext";
import {
  FiSearch,
  FiCheck,
  FiAlertCircle,
  FiUploadCloud,
  FiDownload,
  FiCheckCircle,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiRotateCcw,
  FiCreditCard,
  FiX,
  FiFileText,
  FiPlusCircle,
} from "react-icons/fi";

const DEFAULT_DEPARTMENTS = [
  "Finance",
  "Hostel & Mess",
  "Library",
  "Sports",
  "Academic",
  "Examination",
];

const DuesManagement = ({
  dueRegNo,
  setDueRegNo,
  dueDepartment,
  setDueDepartment,
  dueAmount,
  setDueAmount,
  dueDetails,
  setDueDetails,
  studentDues = [],
  searchedStudent,
  dueLoading,
  onSearchDues,
  onCreateDue,
  onClearDue,
}) => {
  const { token } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [searchError, setSearchError] = useState("");
  const [createErrors, setCreateErrors] = useState({});

  // Mode switcher for adding due: "single" | "import"
  const [activeMode, setActiveMode] = useState("single");
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Master Table State
  const [allDues, setAllDues] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const [tablePagination, setTablePagination] = useState({
    total: 0,
    totalPages: 1,
    currentPage: 1,
    limit: 10,
  });
  const [tableStats, setTableStats] = useState({
    totalDues: 0,
    pendingDues: 0,
    clearedDues: 0,
  });

  // Filters for the Master Table
  const [tableSearch, setTableSearch] = useState("");
  const [localTableSearch, setLocalTableSearch] = useState("");
  const [tableFilterStatus, setTableFilterStatus] = useState("all");
  const [tableFilterDept, setTableFilterDept] = useState("all");

  // Fetch dynamic departments list from database
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE}/auth/departments`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (res.data && res.data.data) {
          setDepartments(res.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch departments in DuesManagement:", err);
      }
    };
    if (token) {
      fetchDepartments();
    }
  }, [token]);

  // Combined list of default + custom departments from DB
  const departmentOptions = React.useMemo(() => {
    const list = [...DEFAULT_DEPARTMENTS];
    departments.forEach((d) => {
      const name = d.department || d;
      if (
        name &&
        !list.some((item) => item.toLowerCase() === name.toLowerCase())
      ) {
        list.push(name);
      }
    });
    return list;
  }, [departments]);

  // Default to first department if empty
  useEffect(() => {
    if (!dueDepartment && departmentOptions.length > 0) {
      setDueDepartment(departmentOptions[0]);
    }
  }, [dueDepartment, departmentOptions, setDueDepartment]);

  // Fetch all dues for the master table with server-side pagination and filters
  const fetchAllDues = useCallback(
    async (params = {}) => {
      if (!token) return;
      try {
        setTableLoading(true);
        const queryPage = params.page !== undefined ? params.page : tablePagination.currentPage;
        const querySearch = params.search !== undefined ? params.search : tableSearch;
        const queryStatus = params.status !== undefined ? params.status : tableFilterStatus;
        const queryDept = params.dept !== undefined ? params.dept : tableFilterDept;

        const queryParams = new URLSearchParams();
        queryParams.append("page", queryPage);
        queryParams.append("limit", 10);
        if (querySearch && querySearch.trim()) queryParams.append("search", querySearch.trim());
        if (queryStatus && queryStatus !== "all") queryParams.append("status", queryStatus);
        if (queryDept && queryDept !== "all") queryParams.append("department", queryDept);

        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE}/due/get-all-dues?${queryParams.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (res.data && res.data.data) {
          setAllDues(res.data.data);
          if (res.data.pagination) {
            setTablePagination(res.data.pagination);
          }
          if (res.data.stats) {
            setTableStats(res.data.stats);
          }
        }
      } catch (err) {
        console.error("Failed to fetch all dues:", err);
      } finally {
        setTableLoading(false);
      }
    },
    [token, tablePagination.currentPage, tableSearch, tableFilterStatus, tableFilterDept]
  );

  // Fetch on mount or when filter changes
  useEffect(() => {
    fetchAllDues({ page: 1 });
  }, [fetchAllDues]);

  // Table search submit
  const handleTableSearchSubmit = (e) => {
    e.preventDefault();
    setTableSearch(localTableSearch);
  };

  const handleClearTableSearch = () => {
    setLocalTableSearch("");
    setTableSearch("");
  };

  // Reset table filters
  const handleResetTableFilters = () => {
    setLocalTableSearch("");
    setTableSearch("");
    setTableFilterStatus("all");
    setTableFilterDept("all");
  };

  // Handle Mark Cleared from Master Table
  const handleTableClearDue = async (due) => {
    if (
      !window.confirm(
        `Are you sure you want to mark due of ₹${due.amount_due} (${due.department}) for ${due.reg_no} as Cleared?`
      )
    ) {
      return;
    }

    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE}/due/clear-student-due/${due._id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setActionMessage({
        text: `Due of ₹${due.amount_due} for ${due.reg_no} marked as Cleared!`,
        type: "success",
      });
      fetchAllDues();
      if (dueRegNo && onSearchDues) {
        onSearchDues();
      }
    } catch (err) {
      setActionMessage({
        text: err?.response?.data?.message || "Failed to clear due record",
        type: "danger",
      });
    }
  };

  // Handle Delete from Master Table
  const handleTableDeleteDue = async (due) => {
    if (
      !window.confirm(
        `Are you sure you want to permanently delete this due record of ₹${due.amount_due} (${due.department}) for ${due.reg_no}?`
      )
    ) {
      return;
    }

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE}/due/delete-due/${due._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setActionMessage({
        text: `Due record for ${due.reg_no} deleted successfully!`,
        type: "success",
      });
      fetchAllDues();
      if (dueRegNo && onSearchDues) {
        onSearchDues();
      }
    } catch (err) {
      setActionMessage({
        text: err?.response?.data?.message || "Failed to delete due record",
        type: "danger",
      });
    }
  };

  // Export all filtered dues to CSV
  const handleExportDuesCSV = async () => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("limit", "all");
      if (tableSearch && tableSearch.trim()) queryParams.append("search", tableSearch.trim());
      if (tableFilterStatus && tableFilterStatus !== "all") queryParams.append("status", tableFilterStatus);
      if (tableFilterDept && tableFilterDept !== "all") queryParams.append("department", tableFilterDept);

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/due/get-all-dues?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data && res.data.data && res.data.data.length > 0) {
        const headers = ["Registration Number", "Department", "Amount (INR)", "Details", "Status", "Date Added"];
        const rows = res.data.data.map((d) => [
          `"${d.reg_no || ""}"`,
          `"${d.department || ""}"`,
          d.amount_due || 0,
          `"${(d.details || "").replace(/"/g, '""')}"`,
          d.is_clear ? "Cleared" : "Pending",
          `"${d.createdAt ? new Date(d.createdAt).toLocaleDateString("en-IN") : ""}"`,
        ]);

        const csvContent =
          "data:text/csv;charset=utf-8," +
          [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `dues_export_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert("No dues data found to export with current filters.");
      }
    } catch (err) {
      console.error("Failed to export dues CSV:", err);
      alert("Failed to export dues CSV.");
    }
  };

  // Always sort searched student dues: uncleared first
  const sortedStudentDues = React.useMemo(() => {
    return [...studentDues].sort((a, b) => {
      if (!a.is_clear && b.is_clear) return -1;
      if (a.is_clear && !b.is_clear) return 1;
      return (b._id || "").localeCompare(a._id || "");
    });
  }, [studentDues]);

  // Handle Search submit with custom validation
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!dueRegNo || !dueRegNo.trim()) {
      setSearchError("Please enter a Registration Number");
      return;
    }
    setSearchError("");
    onSearchDues(e);
  };

  // Handle Create Due submit with custom validation
  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!dueRegNo || !dueRegNo.trim()) {
      errors.reg_no = "Registration Number is required";
    }
    if (!dueDepartment || !dueDepartment.trim()) {
      errors.department = "Department is required";
    }
    if (!dueAmount || isNaN(Number(dueAmount)) || Number(dueAmount) <= 0) {
      errors.amount = "Valid amount is required";
    }

    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      return;
    }

    setCreateErrors({});
    await onCreateDue(e);
    fetchAllDues();
  };

  // Download Sample CSV Template for Dues (RFC-4180 with UTF-8 BOM)
  const handleDownloadTemplate = () => {
    const headers = ["reg_no", "department", "amount_due", "details"];
    const escapeVal = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const sampleRows = [
      ["1234567", "Finance", "500", "Convocation fee dues"],
      ["1234568", "Library", "250", "Library fine"],
      ["1234569", "Hostel & Mess", "1200", "Mess fee clearance"],
      ["1234570", "Sports", "300", "Sports equipment fee"],
    ];
    const csvContent =
      "\uFEFF" +
      [
        headers.map(escapeVal).join(","),
        ...sampleRows.map((r) => r.map(escapeVal).join(",")),
      ].join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "dues_import_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Drag & Drop Handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setImportFile(e.dataTransfer.files[0]);
      setImportResult(null);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImportFile(e.target.files[0]);
      setImportResult(null);
    }
  };

  // Submit Bulk Import
  const handleImportSubmit = async (e) => {
    e.preventDefault();
    if (!importFile) return;

    const postData = new FormData();
    postData.append("file", importFile);
    if (dueDepartment) {
      postData.append("default_department", dueDepartment);
    }

    try {
      setImportLoading(true);
      setImportResult(null);
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/due/bulk-import-dues`,
        postData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setImportResult({
        success: true,
        message: res.data?.message || "Import completed successfully!",
        data: res.data,
      });
      setImportFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (dueRegNo) {
        onSearchDues();
      }
      fetchAllDues({ page: 1 });
    } catch (err) {
      setImportResult({
        success: false,
        message:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to import dues. Please check file columns and format.",
      });
    } finally {
      setImportLoading(false);
    }
  };

  // Helper for pagination page buttons
  const getPaginationPages = () => {
    const totalPages = tablePagination.totalPages || 1;
    const currentPage = tablePagination.currentPage || 1;
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(
          1,
          "...",
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    return pages;
  };

  const totalDuesCount = tablePagination.total || 0;
  const startRecord =
    totalDuesCount === 0
      ? 0
      : (tablePagination.currentPage - 1) * tablePagination.limit + 1;
  const endRecord = Math.min(
    tablePagination.currentPage * tablePagination.limit,
    totalDuesCount
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Action alert message if any */}
      {actionMessage && (
        <div
          style={{
            padding: "12px 18px",
            borderRadius: "8px",
            backgroundColor:
              actionMessage.type === "success" ? "#ecfdf5" : "#fef2f2",
            color: actionMessage.type === "success" ? "#065f46" : "#991b1b",
            border:
              actionMessage.type === "success"
                ? "1px solid #a7f3d0"
                : "1px solid #fecaca",
            fontSize: "13.5px",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>{actionMessage.text}</span>
          <button
            type="button"
            onClick={() => setActionMessage(null)}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "inherit",
              fontSize: "15px",
              padding: "0 4px",
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* TOP SECTION: Split Columns (Quick Inspection & Add/Import Form) */}
      <div className="admin-split-grid">
        {/* Left Card: Search & Inspect Student Dues */}
        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            padding: "22px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            border: "1px solid #e2e8f0",
          }}
        >
          <div
            style={{
              fontSize: "15px",
              fontWeight: 600,
              color: "#0f172a",
              marginBottom: "14px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FiSearch size={16} color="#e06d15" />
            <span>Search Student Dues</span>
          </div>

          {/* Compact Search Form */}
          <form
            noValidate
            onSubmit={handleSearchSubmit}
            style={{ marginBottom: "14px" }}
          >
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type="text"
                placeholder="Enter Registration Number (e.g. 209301099)"
                value={dueRegNo}
                onChange={(e) => {
                  setDueRegNo(e.target.value);
                  if (searchError) setSearchError("");
                  if (createErrors.reg_no) {
                    setCreateErrors({ ...createErrors, reg_no: null });
                  }
                }}
                style={{
                  flex: 1,
                  height: "38px",
                  padding: "0 12px",
                  borderRadius: "6px",
                  border: searchError
                    ? "1px solid #ef4444"
                    : "1px solid #cbd5e1",
                  backgroundColor: searchError ? "#fff5f5" : "#f8fafc",
                  fontSize: "13px",
                  color: "#1e293b",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
              <button
                type="submit"
                disabled={dueLoading}
                style={{
                  height: "38px",
                  padding: "0 18px",
                  backgroundColor: "#0f172a",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "13px",
                  borderRadius: "6px",
                  border: "none",
                  cursor: dueLoading ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                  transition: "background-color 0.15s ease",
                }}
              >
                {dueLoading ? "Searching..." : "Search"}
              </button>
            </div>
            {searchError && (
              <div
                style={{
                  color: "#dc2626",
                  fontSize: "11.5px",
                  marginTop: "4px",
                }}
              >
                {searchError}
              </div>
            )}
          </form>

          {/* Compact Student Header Preview */}
          {searchedStudent && (
            <div
              style={{
                backgroundColor: "#f8fafc",
                padding: "10px 14px",
                borderRadius: "8px",
                marginBottom: "14px",
                border: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <span
                  style={{
                    fontWeight: 600,
                    color: "#0f172a",
                    fontSize: "13.5px",
                  }}
                >
                  {searchedStudent.student_name}
                </span>{" "}
                <span
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    fontWeight: 500,
                  }}
                >
                  ({searchedStudent.reg_no})
                </span>
                <div
                  style={{
                    fontSize: "11.5px",
                    color: "#64748b",
                    marginTop: "2px",
                  }}
                >
                  {searchedStudent.programme} • {searchedStudent.school}
                </div>
              </div>
              <span
                style={{
                  fontSize: "11.5px",
                  fontWeight: 600,
                  color: studentDues.some((d) => !d.is_clear)
                    ? "#b91c1c"
                    : "#047857",
                  backgroundColor: studentDues.some((d) => !d.is_clear)
                    ? "#fef2f2"
                    : "#ecfdf5",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  border: studentDues.some((d) => !d.is_clear)
                    ? "1px solid #fecaca"
                    : "1px solid #a7f3d0",
                }}
              >
                {studentDues.some((d) => !d.is_clear)
                  ? "Pending Dues"
                  : "Dues Cleared"}
              </span>
            </div>
          )}

          {/* Dues List Section Header */}
          <div
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: "#475569",
              marginBottom: "8px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Dues Records for Student</span>
            {studentDues.length > 0 && (
              <span style={{ fontSize: "11.5px", color: "#94a3b8" }}>
                {studentDues.length} record(s)
              </span>
            )}
          </div>

          {studentDues.length === 0 ? (
            <div
              style={{
                padding: "22px 16px",
                textAlign: "center",
                color: "#94a3b8",
                fontSize: "12.5px",
                background: "#f8fafc",
                borderRadius: "6px",
                border: "1px dashed #cbd5e1",
              }}
            >
              {dueRegNo
                ? "No dues found for this student."
                : "Search a student above to inspect individual dues."}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                maxHeight: "300px",
                overflowY: "auto",
                paddingRight: "2px",
              }}
            >
              {sortedStudentDues.map((d) => (
                <div
                  key={d._id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "9px 12px",
                    borderRadius: "6px",
                    backgroundColor: d.is_clear ? "#f8fafc" : "#fff7ed",
                    border: d.is_clear
                      ? "1px solid #e2e8f0"
                      : "1px solid #fed7aa",
                    transition: "all 0.15s ease",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0, paddingRight: "10px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          color: "#0f172a",
                          fontSize: "13px",
                        }}
                      >
                        {d.department}
                      </span>
                      <span style={{ color: "#cbd5e1", fontSize: "12px" }}>
                        •
                      </span>
                      <span
                        style={{
                          fontWeight: 600,
                          color: d.is_clear ? "#64748b" : "#c2410c",
                          fontSize: "12px",
                          backgroundColor: d.is_clear ? "#f1f5f9" : "#ffedd5",
                          padding: "1px 6px",
                          borderRadius: "4px",
                          border: d.is_clear
                            ? "1px solid #e2e8f0"
                            : "1px solid #fed7aa",
                        }}
                      >
                        ₹{d.amount_due}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "11.5px",
                        color: "#64748b",
                        marginTop: "1px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={d.details || "Administrative hold"}
                    >
                      {d.details || "Administrative hold"}
                    </div>
                  </div>

                  {/* Status / Action Button */}
                  <div style={{ flexShrink: 0 }}>
                    {d.is_clear ? (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          padding: "2px 8px",
                          fontSize: "11px",
                          fontWeight: 500,
                          borderRadius: "12px",
                          backgroundColor: "#ecfdf5",
                          color: "#047857",
                          border: "1px solid #a7f3d0",
                        }}
                      >
                        <FiCheck size={12} /> Cleared
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          onClearDue(d);
                          setTimeout(() => fetchAllDues(), 500);
                        }}
                        title={`Clear ₹${d.amount_due} due`}
                        style={{
                          height: "28px",
                          padding: "0 10px",
                          backgroundColor: "#059669",
                          color: "#fff",
                          border: "none",
                          borderRadius: "5px",
                          fontSize: "11.5px",
                          fontWeight: 500,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          transition: "background-color 0.15s ease",
                        }}
                      >
                        <FiCheck size={12} /> Mark Cleared
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Card: Add Due / Bulk Import Form */}
        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            padding: "22px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            border: "1px solid #e2e8f0",
          }}
        >
          {/* Top Header & Mode Toggle */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
              borderBottom: "1px solid #f1f5f9",
              paddingBottom: "12px",
            }}
          >
            <div
              style={{
                fontSize: "15px",
                fontWeight: 600,
                color: "#0f172a",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <FiPlusCircle size={16} color="#e06d15" />
              <span>
                {activeMode === "single"
                  ? "Add Penalty / Due"
                  : "Bulk Import Student Dues"}
              </span>
            </div>

            {/* Mode Switcher */}
            <div
              style={{
                display: "flex",
                background: "#f1f5f9",
                padding: "3px",
                borderRadius: "8px",
                gap: "4px",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setActiveMode("single");
                  setImportResult(null);
                }}
                style={{
                  padding: "5px 12px",
                  fontSize: "12px",
                  fontWeight: 500,
                  borderRadius: "6px",
                  border: "none",
                  cursor: "pointer",
                  backgroundColor:
                    activeMode === "single" ? "#fff" : "transparent",
                  color: activeMode === "single" ? "#ea580c" : "#64748b",
                  boxShadow:
                    activeMode === "single"
                      ? "0 1px 3px rgba(0,0,0,0.08)"
                      : "none",
                  transition: "all 0.15s ease",
                }}
              >
                Single Entry
              </button>
              <button
                type="button"
                onClick={() => {
                  setActiveMode("import");
                  setImportResult(null);
                }}
                style={{
                  padding: "5px 12px",
                  fontSize: "12px",
                  fontWeight: 500,
                  borderRadius: "6px",
                  border: "none",
                  cursor: "pointer",
                  backgroundColor:
                    activeMode === "import" ? "#fff" : "transparent",
                  color: activeMode === "import" ? "#ea580c" : "#64748b",
                  boxShadow:
                    activeMode === "import"
                      ? "0 1px 3px rgba(0,0,0,0.08)"
                      : "none",
                  transition: "all 0.15s ease",
                }}
              >
                Import Excel / CSV
              </button>
            </div>
          </div>

          {/* MODE 1: SINGLE ENTRY FORM */}
          {activeMode === "single" && (
            <form noValidate onSubmit={handleCreateSubmit}>
              {/* Registration Number Field */}
              <div style={{ marginBottom: "12px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "12.5px",
                    fontWeight: 500,
                    color: createErrors.reg_no ? "#dc2626" : "#475569",
                    marginBottom: "5px",
                  }}
                >
                  Student Registration Number
                </label>
                <input
                  type="text"
                  placeholder="e.g. 209301099"
                  value={dueRegNo}
                  onChange={(e) => {
                    setDueRegNo(e.target.value);
                    if (createErrors.reg_no) {
                      setCreateErrors({ ...createErrors, reg_no: null });
                    }
                  }}
                  style={{
                    width: "100%",
                    height: "38px",
                    padding: "0 12px",
                    border: createErrors.reg_no
                      ? "1px solid #ef4444"
                      : "1px solid #cbd5e1",
                    backgroundColor: createErrors.reg_no
                      ? "#fff5f5"
                      : "#f8fafc",
                    borderRadius: "6px",
                    fontSize: "13px",
                    color: "#1e293b",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
                {createErrors.reg_no && (
                  <div
                    style={{
                      color: "#dc2626",
                      fontSize: "11px",
                      marginTop: "3px",
                    }}
                  >
                    {createErrors.reg_no}
                  </div>
                )}
              </div>

              {/* Department & Amount Row */}
              <div
                className="admin-form-two-col"
                style={{
                  marginBottom: "12px",
                }}
              >
                {/* Department Select */}
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "12.5px",
                      fontWeight: 500,
                      color: createErrors.department ? "#dc2626" : "#475569",
                      marginBottom: "5px",
                    }}
                  >
                    Department
                  </label>
                  <select
                    value={dueDepartment}
                    onChange={(e) => {
                      setDueDepartment(e.target.value);
                      if (createErrors.department) {
                        setCreateErrors({
                          ...createErrors,
                          department: null,
                        });
                      }
                    }}
                    style={{
                      width: "100%",
                      height: "38px",
                      padding: "0 10px",
                      borderRadius: "6px",
                      border: createErrors.department
                        ? "1px solid #ef4444"
                        : "1px solid #cbd5e1",
                      backgroundColor: createErrors.department
                        ? "#fff5f5"
                        : "#f8fafc",
                      fontSize: "13px",
                      color: "#334155",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  >
                    {departmentOptions.map((deptName) => (
                      <option key={deptName} value={deptName}>
                        {deptName}
                      </option>
                    ))}
                  </select>
                  {createErrors.department && (
                    <div
                      style={{
                        color: "#dc2626",
                        fontSize: "11px",
                        marginTop: "3px",
                      }}
                    >
                      {createErrors.department}
                    </div>
                  )}
                </div>

                {/* Due Amount */}
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "12.5px",
                      fontWeight: 500,
                      color: createErrors.amount ? "#dc2626" : "#475569",
                      marginBottom: "5px",
                    }}
                  >
                    Due Amount (₹)
                  </label>
                  <input
                    type="number"
                    placeholder="Amount (e.g. 500)"
                    value={dueAmount}
                    onChange={(e) => {
                      setDueAmount(e.target.value);
                      if (createErrors.amount) {
                        setCreateErrors({ ...createErrors, amount: null });
                      }
                    }}
                    style={{
                      width: "100%",
                      height: "38px",
                      padding: "0 12px",
                      border: createErrors.amount
                        ? "1px solid #ef4444"
                        : "1px solid #cbd5e1",
                      backgroundColor: createErrors.amount
                        ? "#fff5f5"
                        : "#f8fafc",
                      borderRadius: "6px",
                      fontSize: "13px",
                      color: "#1e293b",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  {createErrors.amount && (
                    <div
                      style={{
                        color: "#dc2626",
                        fontSize: "11px",
                        marginTop: "3px",
                      }}
                    >
                      {createErrors.amount}
                    </div>
                  )}
                </div>
              </div>

              {/* Details / Reason */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "12.5px",
                    fontWeight: 500,
                    color: "#475569",
                    marginBottom: "5px",
                  }}
                >
                  Remarks / Reason
                </label>
                <textarea
                  placeholder="e.g. Library book fine / Mess clearance hold"
                  value={dueDetails}
                  onChange={(e) => setDueDetails(e.target.value)}
                  rows={2}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    backgroundColor: "#f8fafc",
                    outline: "none",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                  }}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={dueLoading}
                style={{
                  width: "100%",
                  height: "40px",
                  backgroundColor: "#ea580c",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: "13.5px",
                  borderRadius: "6px",
                  border: "none",
                  cursor: dueLoading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background-color 0.15s ease",
                }}
              >
                {dueLoading ? "Adding Due..." : "Add Due Record"}
              </button>
            </form>
          )}

          {/* MODE 2: BULK IMPORT EXCEL / CSV */}
          {activeMode === "import" && (
            <div>
              {/* Result Alert if any */}
              {importResult && (
                <div
                  style={{
                    padding: "12px 14px",
                    borderRadius: "8px",
                    marginBottom: "16px",
                    backgroundColor: importResult.success
                      ? "#f0fdf4"
                      : "#fef2f2",
                    border: importResult.success
                      ? "1px solid #86efac"
                      : "1px solid #fca5a5",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: importResult.success ? "#166534" : "#991b1b",
                      fontSize: "13px",
                      fontWeight: 500,
                      marginBottom: importResult.data ? "8px" : "0",
                    }}
                  >
                    {importResult.success ? (
                      <FiCheckCircle size={16} color="#16a34a" />
                    ) : (
                      <FiAlertCircle size={16} color="#dc2626" />
                    )}
                    <span>{importResult.message}</span>
                  </div>

                  {importResult.data && (
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: "10px",
                          fontSize: "11.5px",
                          fontWeight: 600,
                          backgroundColor: "#dcfce7",
                          color: "#15803d",
                          border: "1px solid #bbf7d0",
                        }}
                      >
                        ✓ {importResult.data.inserted || 0} Dues Added
                      </span>

                      {importResult.data.duplicates > 0 && (
                        <span
                          style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: "10px",
                            fontSize: "11.5px",
                            fontWeight: 600,
                            backgroundColor: "#fef3c7",
                            color: "#b45309",
                            border: "1px solid #fde68a",
                          }}
                        >
                          ⚠ {importResult.data.duplicates} Duplicates Skipped
                        </span>
                      )}

                      {importResult.data.invalid > 0 && (
                        <span
                          style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: "10px",
                            fontSize: "11.5px",
                            fontWeight: 600,
                            backgroundColor: "#fee2e2",
                            color: "#991b1b",
                            border: "1px solid #fecaca",
                          }}
                        >
                          ✕ {importResult.data.invalid} Invalid
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Drag and Drop Zone */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() =>
                  fileInputRef.current && fileInputRef.current.click()
                }
                style={{
                  border: `2px dashed ${dragActive ? "#ea580c" : "#cbd5e1"}`,
                  borderRadius: "10px",
                  padding: "26px 16px",
                  textAlign: "center",
                  backgroundColor: dragActive ? "#fff7ed" : "#f8fafc",
                  cursor: "pointer",
                  marginBottom: "14px",
                  transition: "all 0.2s ease",
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx, .xls, .csv"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                <FiUploadCloud
                  size={32}
                  color={dragActive ? "#ea580c" : "#94a3b8"}
                  style={{ marginBottom: "8px" }}
                />
                <div
                  style={{
                    fontSize: "13.5px",
                    fontWeight: 600,
                    color: "#1e293b",
                    marginBottom: "3px",
                  }}
                >
                  {importFile
                    ? importFile.name
                    : "Click to browse or drop dues file here"}
                </div>
                <div style={{ fontSize: "11.5px", color: "#64748b" }}>
                  {importFile
                    ? `${(importFile.size / 1024).toFixed(1)} KB`
                    : "Supports .xlsx, .xls, and .csv files (Columns: reg_no, department, amount_due, details)"}
                </div>
              </div>

              {/* Action Row */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                {/* Download Template Button */}
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "7px 12px",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#475569",
                    background: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    cursor: "pointer",
                    transition: "background 0.15s ease",
                  }}
                >
                  <FiDownload size={13} /> Sample Template
                </button>

                {/* Upload Button */}
                <button
                  type="button"
                  disabled={!importFile || importLoading}
                  onClick={handleImportSubmit}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "7px 16px",
                    fontSize: "12.5px",
                    fontWeight: 600,
                    color: "#fff",
                    backgroundColor:
                      !importFile || importLoading ? "#fdba74" : "#ea580c",
                    border: "none",
                    borderRadius: "6px",
                    cursor:
                      !importFile || importLoading ? "not-allowed" : "pointer",
                    transition: "background-color 0.15s ease",
                  }}
                >
                  <FiUploadCloud size={14} />
                  {importLoading ? "Importing..." : "Upload & Import"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MASTER SECTION: FULL DUES & PENALTIES DIRECTORY TABLE */}
      <div className="admin-card">
        {/* Header Title & Summary Metrics */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "14px",
            marginBottom: "18px",
            borderBottom: "1px solid #f1f5f9",
            paddingBottom: "16px",
          }}
        >
          <div>
            <h4
              style={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#0f172a",
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <FiCreditCard color="#ea580c" /> All Dues & Penalties Directory
            </h4>
            <div
              style={{
                fontSize: "12.5px",
                color: "#64748b",
                marginTop: "3px",
              }}
            >
              View, search, filter, clear, or remove student financial holds and department dues.
            </div>
          </div>

          {/* Quick Metrics Badges */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                padding: "6px 14px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12.5px",
                fontWeight: 600,
                color: "#334155",
              }}
            >
              <FiFileText color="#64748b" /> Total: {tableStats.totalDues}
            </div>

            <div
              style={{
                background: "#fff7ed",
                border: "1px solid #fed7aa",
                padding: "6px 14px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12.5px",
                fontWeight: 600,
                color: "#c2410c",
              }}
            >
              <FiAlertCircle color="#ea580c" /> Pending Holds: {tableStats.pendingDues}
            </div>

            <div
              style={{
                background: "#ecfdf5",
                border: "1px solid #a7f3d0",
                padding: "6px 14px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12.5px",
                fontWeight: 600,
                color: "#047857",
              }}
            >
              <FiCheckCircle color="#10b981" /> Cleared: {tableStats.clearedDues}
            </div>
          </div>
        </div>

        {/* Toolbar: Search, Filters, Reset & Export */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            marginBottom: "18px",
          }}
        >
          {/* Left: Search input */}
          <form
            onSubmit={handleTableSearchSubmit}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flex: "1 1 280px",
              maxWidth: "420px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                height: "38px",
                border: "1px solid #cbd5e1",
                borderRadius: "8px",
                padding: "0 10px",
                backgroundColor: "#f8fafc",
                boxSizing: "border-box",
              }}
            >
              <FiSearch color="#94a3b8" size={15} style={{ flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search reg no, details, department..."
                value={localTableSearch}
                onChange={(e) => setLocalTableSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0 8px",
                  border: "none",
                  outline: "none",
                  backgroundColor: "transparent",
                  fontSize: "13px",
                  color: "#1e293b",
                }}
              />
              {localTableSearch && (
                <button
                  type="button"
                  onClick={handleClearTableSearch}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#94a3b8",
                    cursor: "pointer",
                    padding: "2px",
                    fontSize: "13px",
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                  title="Clear Search"
                >
                  <FiX />
                </button>
              )}
            </div>
            <button
              type="submit"
              style={{
                height: "38px",
                padding: "0 14px",
                backgroundColor: "#ea580c",
                color: "#fff",
                fontWeight: 600,
                fontSize: "13px",
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                whiteSpace: "nowrap",
                boxShadow: "0 2px 6px rgba(234, 88, 12, 0.25)",
              }}
            >
              <FiSearch size={13} /> Search
            </button>
          </form>

          {/* Right: Filters & Export */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            {/* Status Filter */}
            <select
              value={tableFilterStatus}
              onChange={(e) => setTableFilterStatus(e.target.value)}
              style={{
                height: "38px",
                padding: "0 10px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
                backgroundColor: "#f8fafc",
                fontWeight: 500,
                color: "#334155",
                cursor: "pointer",
                outline: "none",
              }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending Holds</option>
              <option value="cleared">Cleared Dues</option>
            </select>

            {/* Department Filter */}
            <select
              value={tableFilterDept}
              onChange={(e) => setTableFilterDept(e.target.value)}
              style={{
                height: "38px",
                padding: "0 10px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                fontSize: "13px",
                backgroundColor: "#f8fafc",
                fontWeight: 500,
                color: "#334155",
                cursor: "pointer",
                outline: "none",
              }}
            >
              <option value="all">All Departments</option>
              {departmentOptions.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>

            {/* Reset Filters */}
            {(tableSearch ||
              tableFilterStatus !== "all" ||
              tableFilterDept !== "all") && (
              <button
                type="button"
                onClick={handleResetTableFilters}
                style={{
                  height: "38px",
                  padding: "0 12px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  backgroundColor: "#fff",
                  color: "#64748b",
                  fontSize: "13px",
                  fontWeight: 500,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                title="Reset Filters"
              >
                <FiRotateCcw size={13} /> Reset
              </button>
            )}

            {/* Export CSV Button */}
            <button
              type="button"
              onClick={handleExportDuesCSV}
              style={{
                height: "38px",
                padding: "0 14px",
                borderRadius: "8px",
                border: "1px solid #0f172a",
                backgroundColor: "#0f172a",
                color: "#fff",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.15s ease",
              }}
            >
              <FiDownload size={14} /> Export CSV
            </button>
          </div>
        </div>

        {/* Table View */}
        <div className="admin-table-wrapper">
          <table
            className="admin-table"
            style={{
              borderCollapse: "collapse",
              textAlign: "left",
              fontSize: "13px",
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: "#f8fafc",
                  borderBottom: "2px solid #e2e8f0",
                  color: "#475569",
                  fontWeight: 600,
                  fontSize: "12px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                <th style={{ padding: "12px 14px", width: "45px" }}>#</th>
                <th style={{ padding: "12px 14px" }}>Registration No</th>
                <th style={{ padding: "12px 14px" }}>Department</th>
                <th style={{ padding: "12px 14px" }}>Amount</th>
                <th style={{ padding: "12px 14px" }}>Remarks / Details</th>
                <th style={{ padding: "12px 14px" }}>Status</th>
                <th style={{ padding: "12px 14px" }}>Created</th>
                <th style={{ padding: "12px 14px", textAlign: "right" }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {tableLoading ? (
                <tr>
                  <td
                    colSpan="8"
                    style={{
                      padding: "40px",
                      textAlign: "center",
                      color: "#64748b",
                    }}
                  >
                    <Spinner
                      animation="border"
                      size="sm"
                      style={{ marginRight: "8px", color: "#ea580c" }}
                    />
                    Loading dues data...
                  </td>
                </tr>
              ) : allDues.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    style={{
                      padding: "40px 20px",
                      textAlign: "center",
                      color: "#94a3b8",
                      fontSize: "13.5px",
                    }}
                  >
                    No dues or penalty records found matching the current criteria.
                  </td>
                </tr>
              ) : (
                allDues.map((due, idx) => {
                  const rowNumber =
                    (tablePagination.currentPage - 1) * tablePagination.limit +
                    idx +
                    1;
                  return (
                    <tr
                      key={due._id || idx}
                      style={{
                        borderBottom: "1px solid #f1f5f9",
                        backgroundColor: due.is_clear ? "#fff" : "#fffbf5",
                        transition: "background-color 0.15s ease",
                      }}
                    >
                      {/* Index */}
                      <td
                        style={{
                          padding: "12px 14px",
                          color: "#94a3b8",
                          fontWeight: 500,
                        }}
                      >
                        {rowNumber}
                      </td>

                      {/* Reg No */}
                      <td style={{ padding: "12px 14px" }}>
                        <span
                          style={{
                            fontWeight: 600,
                            color: "#0f172a",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setDueRegNo(due.reg_no);
                            onSearchDues();
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                          title="Click to inspect student dues above"
                        >
                          {due.reg_no}
                        </span>
                      </td>

                      {/* Department Badge */}
                      <td style={{ padding: "12px 14px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "3px 8px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: 600,
                            backgroundColor: "#f1f5f9",
                            color: "#334155",
                            border: "1px solid #e2e8f0",
                          }}
                        >
                          {due.department}
                        </span>
                      </td>

                      {/* Amount Due */}
                      <td style={{ padding: "12px 14px" }}>
                        <span
                          style={{
                            fontWeight: 700,
                            fontSize: "13.5px",
                            color: due.is_clear ? "#64748b" : "#c2410c",
                          }}
                        >
                          ₹{due.amount_due}
                        </span>
                      </td>

                      {/* Remarks / Details */}
                      <td
                        style={{
                          padding: "12px 14px",
                          maxWidth: "240px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "#475569",
                        }}
                        title={due.details || "Administrative hold"}
                      >
                        {due.details || "Administrative hold"}
                      </td>

                      {/* Status */}
                      <td style={{ padding: "12px 14px" }}>
                        {due.is_clear ? (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              padding: "3px 10px",
                              borderRadius: "12px",
                              fontSize: "11.5px",
                              fontWeight: 600,
                              backgroundColor: "#ecfdf5",
                              color: "#047857",
                              border: "1px solid #a7f3d0",
                            }}
                          >
                            <FiCheck size={12} /> Cleared
                          </span>
                        ) : (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              padding: "3px 10px",
                              borderRadius: "12px",
                              fontSize: "11.5px",
                              fontWeight: 600,
                              backgroundColor: "#fff7ed",
                              color: "#c2410c",
                              border: "1px solid #fed7aa",
                            }}
                          >
                            <FiAlertCircle size={12} /> Pending Hold
                          </span>
                        )}
                      </td>

                      {/* Date */}
                      <td
                        style={{
                          padding: "12px 14px",
                          color: "#64748b",
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {due.createdAt
                          ? new Date(due.createdAt).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                          : "-"}
                      </td>

                      {/* Actions */}
                      <td style={{ padding: "12px 14px", textAlign: "right" }}>
                        <div
                          style={{
                            display: "inline-flex",
                            gap: "6px",
                            justifyContent: "flex-end",
                          }}
                        >
                          {!due.is_clear && (
                            <button
                              type="button"
                              onClick={() => handleTableClearDue(due)}
                              title="Mark this due as Cleared"
                              style={{
                                height: "30px",
                                padding: "0 10px",
                                backgroundColor: "#059669",
                                color: "#fff",
                                border: "none",
                                borderRadius: "6px",
                                fontSize: "12px",
                                fontWeight: 500,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                transition: "background-color 0.15s ease",
                              }}
                            >
                              <FiCheck size={12} /> Clear
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => handleTableDeleteDue(due)}
                            title="Delete this record"
                            style={{
                              height: "30px",
                              width: "30px",
                              backgroundColor: "#fee2e2",
                              color: "#dc2626",
                              border: "1px solid #fca5a5",
                              borderRadius: "6px",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.15s ease",
                            }}
                          >
                            <FiTrash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Master Table Pagination */}
        <div className="admin-pagination-wrapper">
          <div className="admin-pagination-info">
            Showing{" "}
            <span style={{ fontWeight: 600, color: "#1e293b" }}>
              {startRecord}
            </span>{" "}
            to{" "}
            <span style={{ fontWeight: 600, color: "#1e293b" }}>
              {endRecord}
            </span>{" "}
            of{" "}
            <span style={{ fontWeight: 600, color: "#1e293b" }}>
              {totalDuesCount}
            </span>{" "}
            records
          </div>

          {/* Pagination Controls */}
          {tablePagination.totalPages > 1 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              {/* First Page */}
              <button
                type="button"
                disabled={tablePagination.currentPage <= 1 || tableLoading}
                onClick={() => fetchAllDues({ page: 1 })}
                style={{
                  height: "32px",
                  width: "32px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  backgroundColor: "#fff",
                  color:
                    tablePagination.currentPage <= 1 ? "#cbd5e1" : "#475569",
                  cursor:
                    tablePagination.currentPage <= 1
                      ? "not-allowed"
                      : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title="First Page"
              >
                <FiChevronsLeft size={14} />
              </button>

              {/* Prev Page */}
              <button
                type="button"
                disabled={tablePagination.currentPage <= 1 || tableLoading}
                onClick={() =>
                  fetchAllDues({ page: tablePagination.currentPage - 1 })
                }
                style={{
                  height: "32px",
                  width: "32px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  backgroundColor: "#fff",
                  color:
                    tablePagination.currentPage <= 1 ? "#cbd5e1" : "#475569",
                  cursor:
                    tablePagination.currentPage <= 1
                      ? "not-allowed"
                      : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title="Previous Page"
              >
                <FiChevronLeft size={14} />
              </button>

              {/* Page Number Buttons */}
              {getPaginationPages().map((pageItem, pIdx) => {
                if (pageItem === "...") {
                  return (
                    <span
                      key={`dots-${pIdx}`}
                      style={{
                        padding: "0 6px",
                        color: "#94a3b8",
                        fontSize: "13px",
                      }}
                    >
                      ...
                    </span>
                  );
                }
                const isCurrent = pageItem === tablePagination.currentPage;
                return (
                  <button
                    key={`page-${pageItem}`}
                    type="button"
                    disabled={tableLoading}
                    onClick={() => fetchAllDues({ page: pageItem })}
                    style={{
                      height: "32px",
                      minWidth: "32px",
                      padding: "0 6px",
                      borderRadius: "6px",
                      border: isCurrent
                        ? "1px solid #ea580c"
                        : "1px solid #cbd5e1",
                      backgroundColor: isCurrent ? "#ea580c" : "#fff",
                      color: isCurrent ? "#fff" : "#334155",
                      fontWeight: isCurrent ? 700 : 500,
                      fontSize: "13px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {pageItem}
                  </button>
                );
              })}

              {/* Next Page */}
              <button
                type="button"
                disabled={
                  tablePagination.currentPage >= tablePagination.totalPages ||
                  tableLoading
                }
                onClick={() =>
                  fetchAllDues({ page: tablePagination.currentPage + 1 })
                }
                style={{
                  height: "32px",
                  width: "32px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  backgroundColor: "#fff",
                  color:
                    tablePagination.currentPage >= tablePagination.totalPages
                      ? "#cbd5e1"
                      : "#475569",
                  cursor:
                    tablePagination.currentPage >= tablePagination.totalPages
                      ? "not-allowed"
                      : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title="Next Page"
              >
                <FiChevronRight size={14} />
              </button>

              {/* Last Page */}
              <button
                type="button"
                disabled={
                  tablePagination.currentPage >= tablePagination.totalPages ||
                  tableLoading
                }
                onClick={() =>
                  fetchAllDues({ page: tablePagination.totalPages })
                }
                style={{
                  height: "32px",
                  width: "32px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  backgroundColor: "#fff",
                  color:
                    tablePagination.currentPage >= tablePagination.totalPages
                      ? "#cbd5e1"
                      : "#475569",
                  cursor:
                    tablePagination.currentPage >= tablePagination.totalPages
                      ? "not-allowed"
                      : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title="Last Page"
              >
                <FiChevronsRight size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DuesManagement;
